/**
 * API Client for Speak Client
 */
import * as Sentry from "@sentry/react";
import { DevicePlatform } from "@speakai/shared";

import { API_URL } from "@/config/constants";
import { AUTH_COOKIE_NAMES, getBrowserCookieValue } from "@/lib/auth/cookies";
import { buildLoginRedirectUrl, isAuthRoute } from "@/lib/auth/route-policy";
import { clearLegacyAuthStorage } from "@/lib/auth/storage";
import { getMetaMatchParams } from "@/lib/integrations/utm";
import { isAnalyticsConsentGranted } from "@/lib/integrations/ga4";
import { track, trackFailure } from "@/lib/integrations/sentry-tracking";
import { emitActivityLog } from "@/lib/integrations/activity-events";
import type { RequestMeta } from "@speakai/common/src/api/types";
import type { RequestOptions } from "@speakai/common/src/interfaces/api-http-client";

/** Generic API response shape matching the backend. */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
  message?: string;
  /** HTTP status of a non-OK response (e.g. 409 for optimistic-concurrency conflicts). */
  status?: number;
  // TODO: CHECK WITH TEAM: new optional field on the shared ApiResponse
  /** The parsed body of a failed response, for endpoints that send detail with a failure (a 429's `nextAllowedAt`). */
  errorBody?: unknown;
}

type FailureClass =
  | "abort"
  | "timeout"
  | "offline"
  | "network"
  | "api_error"
  | "api_failed";

/** "rejected": the server refused the refresh; "network": the refresh request never got an answer. */
type RefreshResult = "ok" | "rejected" | "network";

const SESSION_EXPIRED_ERROR = "Session expired. Please sign in again.";

// Cross-tab lock name: concurrent refreshes of one cookie session send an already-rotated token, which the server treats as replay.
const REFRESH_LOCK_NAME = "speak-auth-refresh";
const REFRESH_TIMEOUT_MS = 30_000;
// How many times ONE call may retry after a 401-triggered refresh succeeds before giving
// up on that call specifically. Needed because refreshAttempts (below) resets on every
// refresh success, so it no longer bounds a single endpoint that keeps 401ing even after
// the token was refreshed (see coordinateTokenRefresh).
const MAX_AUTH_RETRIES_PER_CALL = 1;

/** Server errorCodes (all exist in speak-server) that are normal product states, not bugs. */
const EXPECTED_ERROR_CODES: ReadonlySet<string> = new Set([
  "AUTH_OTP_REQUIRED",
  "AUTH_SSO_LINKED",
  "SUBSCRIPTION_PAUSED",
  "SUBSCRIPTION_PAYMENT_INCOMPLETE",
  "ACTIVE_PAID_PLAN",
  "SEAT_LIMIT_REACHED",
  "STREAMING_NOT_SUPPORTED_FOR_INDIVIDUAL",
  "OTP_RESEND_LIMIT",
  "ACCOUNT_EXISTS_PASSWORD",
  "ACCOUNT_EXISTS",
]);

/**
 * User-safe message for a thrown fetch error. The raw browser strings
 * ("Failed to fetch", "NetworkError when attempting to fetch resource",
 * "Load failed") are useless to users and never reach the UI through this
 * helper. The raw text still flows to telemetry via captureNetworkError.
 */
function userMessageForFailure(errorClass: FailureClass): string {
  switch (errorClass) {
    case "offline":
      return "You're offline. Check your connection and try again.";
    case "abort":
      return "Request was canceled.";
    case "timeout":
      return "The request took too long. Please try again.";
    case "network":
    default:
      return "Something went wrong. Please try again.";
  }
}

/**
 * Classify a thrown error from postSSEStream into a FailureClass for telemetry.
 * Recognizes the discriminant strings postSSEStream throws ("offline",
 * "network_after_retry") plus native AbortError DOMException + generic fetch
 * throws. Falls back to "api_error" for explicit server-error throws.
 */
function classifySSEError(error: unknown): FailureClass {
  if (!(error instanceof Error)) return "api_error";
  if (error.name === "AbortError" || /aborted/i.test(error.message)) return "abort";
  if (error.message === "offline") return "offline";
  if (error.message === "network_after_retry") return "network";
  if (/Failed to fetch|NetworkError when attempting|Load failed/i.test(error.message)) {
    return "network";
  }
  return "api_error";
}

/**
 * True when a path segment is a record identifier rather than a route name.
 * Covers the three id shapes the API emits: numeric ids, hex ids (12-char short
 * ids and 24-char ObjectIds), and long alphanumeric codes such as invite slugs.
 */
function isIdSegment(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true;
  if (/^[0-9a-f]{12,}$/i.test(segment)) return true;
  return segment.length >= 12 && /^[A-Za-z0-9]+$/.test(segment) && /\d/.test(segment);
}

interface RequestSignal {
  signal?: AbortSignal;
  cleanup: () => void;
}

function timeoutSignal(ms: number): Required<RequestSignal> {
  if (typeof AbortSignal.timeout === "function") return { signal: AbortSignal.timeout(ms), cleanup: () => undefined };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException("signal timed out", "TimeoutError")), ms);
  return { signal: controller.signal, cleanup: () => clearTimeout(timer) };
}

function anySignal(signals: AbortSignal[]): Required<RequestSignal> {
  if (typeof AbortSignal.any === "function") return { signal: AbortSignal.any(signals), cleanup: () => undefined };
  const controller = new AbortController();
  const listeners: Array<[AbortSignal, () => void]> = [];
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    const listener = () => controller.abort(signal.reason);
    signal.addEventListener("abort", listener, { once: true });
    listeners.push([signal, listener]);
  }
  return {
    signal: controller.signal,
    cleanup: () => listeners.forEach(([signal, listener]) => signal.removeEventListener("abort", listener)),
  };
}

function requestSignal(meta?: RequestOptions): RequestSignal {
  const callerSignal = meta?.signal as AbortSignal | undefined;
  if (meta?.timeoutMs === undefined) return { signal: callerSignal, cleanup: () => undefined };
  const timeout = timeoutSignal(meta.timeoutMs);
  if (!callerSignal) return timeout;
  const combined = anySignal([callerSignal, timeout.signal]);
  return {
    signal: combined.signal,
    cleanup: () => {
      combined.cleanup();
      timeout.cleanup();
    },
  };
}

function isTimeout(error: unknown, signal: AbortSignal | undefined): boolean {
  return (error instanceof Error && error.name === "TimeoutError") || (signal?.reason as Error | undefined)?.name === "TimeoutError";
}

/**
 * Collapse identifier segments in an API path so Sentry grouping stays bounded.
 * `/media/insight/30b4f03c120c` and `/media/insight/21673fec79a8` are the same
 * endpoint failing twice, but an un-normalized path opens a separate issue per
 * id. Returns the path with id-shaped segments replaced by `:id`.
 */
function normalizeApiPath(path: string): string {
  return path
    .split("/")
    .map((segment) => (isIdSegment(segment) ? ":id" : segment))
    .join("/");
}

// Read version from package.json at build time
const APP_VERSION = __APP_VERSION__ || "0.0.0";

// Endpoints where the server reads X-Fb* headers to build the Meta CAPI match-key (mirrors the server's captureLiveAttribution call sites). /auth/register + /auth/sso/* are excluded, they forward attribution via the request body.
const CONVERSION_ENDPOINT_PATTERNS: RegExp[] = [
  /^\/auth\/otp\/verify(?:$|\/)/,
  /^\/payment\/paymentIntent(?:$|\/)/,
  /^\/payment\/addBalanceToAccount(?:$|\/)/,
  // /media upload family (upload/process/analyze/estimate): the MediaUpload Meta event fires later
  // from an async job with no request of its own, so it can't read live headers. Sending X-Fb* here
  // lets the server persist them to the user's snapshot, which that async event then reads. Scoped to
  // the upload flow so cookies don't ride ordinary media reads (list/status/transcript/statistics).
  /^\/media\/(upload|process|analyze|estimate)(?:$|\/)/,
];

/**
 * True when `endpoint` is a route the server reads X-Fb* headers on. `/auth/register`
 * and `/auth/sso/*` are explicitly excluded (they carry attribution in the body); this
 * exclusion is scoped so `/auth/otp/verify` still matches.
 */
function isConversionEndpoint(endpoint: string): boolean {
  const path = endpoint.split("?")[0];
  if (path.startsWith("/auth/register") || path.startsWith("/auth/sso/")) return false;
  return CONVERSION_ENDPOINT_PATTERNS.some((pattern) => pattern.test(path));
}

/**
 * Live Meta pixel match-key headers for conversion endpoints. Read fresh per request
 * (not cached) so a cookie set mid-session (e.g. Meta pixel firing after page load)
 * is picked up on the next conversion call. No event-id header, the registration
 * event_id travels in the body (see features/auth/api.ts `metaEventId`).
 */
function getConversionHeaders(endpoint: string): HeadersInit {
  if (!isConversionEndpoint(endpoint)) return {};
  if (!isAnalyticsConsentGranted()) return {};
  const { fbp, fbc, fbclid } = getMetaMatchParams();
  return {
    ...(fbp && { "X-Fbp": fbp }),
    ...(fbc && { "X-Fbc": fbc }),
    ...(fbclid && { "X-Fbclid": fbclid }),
  };
}


/** True for a 4xx answer whose errorCode marks a state the UI handles by design. */
function isExpectedApiRejection(status: number, data: { errorCode?: unknown }): boolean {
  return status < 500 && typeof data.errorCode === "string" && EXPECTED_ERROR_CODES.has(data.errorCode);
}

class ApiClient {
  private baseUrl: string;
  private accessToken: string = "";
  private refreshToken: string = "";
  private deviceId: string = "";
  private platform: string = DevicePlatform.WEB;

  private refreshPromise: Promise<RefreshResult> | null = null;
  private ipAddress: string = "";
  private refreshAttempts: number = 0;
  private readonly MAX_REFRESH_ATTEMPTS = 3;

  // Latch set once session is dead (failed refresh or user logout). Every
  // subsequent request short-circuits without hitting the network, preventing
  // the cascade where N concurrent components each trigger their own 401 →
  // refresh → redirect cycle while the browser navigation is pending.
  private sessionDead: boolean = false;

  // A missing CSRF cookie means "logged out" for a visitor who never signed in here, but "expired" for a tab that had a session.
  private hadBrowserSession: boolean = false;

  // Proactive refresh: refresh 30s before the access token expires
  // so requests never hit a 401 in normal operation.
  private readonly PROACTIVE_REFRESH_BUFFER_MS = 30_000;
  private readonly MAX_SAFE_TIMEOUT_MS = 2_147_483_647; // setTimeout max (~24.8 days)
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private lastKnownTokenExpiryMs: number | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;

    if (typeof window !== "undefined") {
      const t = localStorage.getItem("accessToken");
      const r = localStorage.getItem("refreshToken");
      if (t) this.accessToken = t;
      if (r) this.refreshToken = r;
      this.hadBrowserSession = this.hasBrowserSession();
    }

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (
          document.visibilityState === "visible" &&
          this.lastKnownTokenExpiryMs
        ) {
          this.scheduleProactiveRefreshFromExpiry(this.lastKnownTokenExpiryMs);
        }
      });
    }
  }

  setToken(token: string) {
    this.accessToken = token;
    this.sessionDead = false;
    if (token) this.scheduleProactiveRefresh(token);
  }

  /** True once this tab's session has ended and the page is redirecting to login. */
  isSessionDead(): boolean {
    return this.sessionDead;
  }

  setRefreshToken(token: string) {
    this.refreshToken = token;
  }

  setDeviceId(id: string) {
    this.deviceId = id;
  }

  setPlatform(platform: string) {
    this.platform = platform;
  }

  setIpAddress(ip: string) {
    this.ipAddress = ip;
  }

  /**
   * Attempt a session token refresh without triggering logout on failure.
   * Used by AuthProvider during session hydration when the access token
   * cookie has expired but the refresh token cookie may still be valid.
   * Unlike coordinateTokenRefresh(), this does NOT redirect to /auth/login
   * on failure, the caller is responsible for handling a false return value.
   */
  async refreshSession(): Promise<boolean> {
    if (this.refreshPromise) {
      return (await this.refreshPromise) === "ok";
    }
    const refreshPromise = this.attemptRefresh();
    this.refreshPromise = refreshPromise;
    try {
      return (await refreshPromise) === "ok";
    } finally {
      if (this.refreshPromise === refreshPromise) {
        this.refreshPromise = null;
      }
    }
  }

  /**
   * Register the access token expiry to enable proactive refresh scheduling.
   * Call this after login or SSO with the `tokenExpiresIn` value from the
   * auth response. Without this, the proactive refresh timer only activates
   * after the first reactive 401 cycle, meaning the initial token always
   * expires silently before any background refresh fires.
   */
  notifyTokenExpiry(tokenExpiresIn: Date | string): void {
    const ms =
      typeof tokenExpiresIn === "string"
        ? new Date(tokenExpiresIn).getTime()
        : tokenExpiresIn.getTime();
    if (ms > Date.now()) {
      this.hadBrowserSession = true;
      this.scheduleProactiveRefreshFromExpiry(ms);
    }
  }

  /**
   * Get app origin.
   */
  private getAppOrigin(): string {
    if (typeof window === "undefined") return "";
    return window.location.origin;
  }

  /**
   * Build request headers. `endpoint` is threaded through (rather than merged by each
   * caller) so every send method, request/upload/postBlob/SSE, picks up Meta CAPI
   * conversion headers from this single shared path.
   */
  private getHeaders(endpoint: string, extraHeaders?: HeadersInit): HeadersInit {
    return {
      "Content-Type": "application/json",
      ...(this.accessToken && { "x-access-token": this.accessToken }),
      ...(this.refreshToken && { "x-refresh-token": this.refreshToken }),
      ...(this.deviceId && { "x-device-id": this.deviceId }),
      "X-App-Platform": this.platform,
      "X-App-Origin": this.getAppOrigin(),
      "X-App-Version": APP_VERSION,
      ...(this.ipAddress && { "X-Forwarded-Key": this.ipAddress }),
      "ngrok-skip-browser-warning": "true",
      ...getConversionHeaders(endpoint),
      ...extraHeaders,
    };
  }

  private getCsrfHeader(method?: string): HeadersInit {
    const normalizedMethod = method?.toUpperCase() || "GET";
    if (typeof window === "undefined") return {};
    if (["GET", "HEAD", "OPTIONS"].includes(normalizedMethod)) return {};

    const csrfToken = getBrowserCookieValue(AUTH_COOKIE_NAMES.csrf);
    return csrfToken ? { "X-CSRF-Token": csrfToken } : {};
  }

  private clearTokens(): void {
    clearLegacyAuthStorage();
    this.accessToken = "";
    this.refreshToken = "";
    this.cancelProactiveRefresh();
  }

  private cancelProactiveRefresh(): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
    this.lastKnownTokenExpiryMs = null;
  }

  /**
   * Decode JWT expiry from a token without verifying the signature.
   * Returns the `exp` claim in milliseconds, or null if unreadable.
   * Only used for scheduling, never for auth decisions.
   */
  private decodeTokenExpiry(token: string): number | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3 || !parts[1]) return null;
      // Restore standard base64 and add padding
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      if (typeof payload.exp !== "number") return null;
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  /**
   * Schedule a proactive token refresh from an absolute expiry timestamp.
   * Used both for initial scheduling and for re-evaluation on tab visibility.
   */
  private scheduleProactiveRefreshFromExpiry(expiresAtMs: number): void {
    this.cancelProactiveRefresh();
    this.lastKnownTokenExpiryMs = expiresAtMs;

    const refreshInMs = Math.min(
      expiresAtMs - Date.now() - this.PROACTIVE_REFRESH_BUFFER_MS,
      this.MAX_SAFE_TIMEOUT_MS,
    );
    if (refreshInMs <= 0) return; // Near/past expiry, let 401 handler deal with it

    this.proactiveRefreshTimer = setTimeout(() => {
      this.proactiveRefreshTimer = null;
      // Proactive refresh, use coordinateTokenRefresh but don't count
      // as a failure-driven attempt (refreshAttempts is only for 401 retries).
      void this.proactiveRefresh();
    }, refreshInMs);
  }

  /**
   * Schedule proactive refresh from a JWT token (decodes exp claim).
   */
  private scheduleProactiveRefresh(accessToken: string): void {
    const expiresAtMs = this.decodeTokenExpiry(accessToken);
    if (!expiresAtMs) return;
    this.scheduleProactiveRefreshFromExpiry(expiresAtMs);
  }

  /**
   * Proactive refresh, fires before token expiry. Does NOT increment
   * refreshAttempts (that counter is only for 401-driven retry loops).
   * On failure, silently falls back to 401-driven refresh.
   */
  private async proactiveRefresh(): Promise<void> {
    // If a refresh is already in progress, skip
    if (this.refreshPromise) return;

    const refreshPromise = this.attemptRefresh();
    this.refreshPromise = refreshPromise;

    try {
      const result = await refreshPromise;
      if (result !== "ok") {
        // Proactive refresh failed, don't logout, let the next real
        // request trigger 401 handling which has proper retry logic.
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Proactive token refresh failed, will retry on next 401",
          level: "warning",
        });
      }
    } finally {
      if (this.refreshPromise === refreshPromise) {
        this.refreshPromise = null;
      }
    }
  }

  /**
   * Attempt to refresh the access token.
   * On success, schedules the next proactive refresh.
   *
   * The server strips tokens from the response body for browser requests
   * (toBrowserAuthPayload). Auth is maintained via httpOnly cookies.
   * When tokens ARE present (non-browser fallback), store them in memory.
   * For proactive refresh scheduling, use the `expiresIn` field if available,
   * otherwise fall back to decoding the JWT exp from the access token.
   *
   * Serialized across tabs with a Web Lock when the browser supports it.
   */
  private async attemptRefresh(): Promise<RefreshResult> {
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!locks) return this.sendRefreshRequest();

    const expiryBeforeWait = getBrowserCookieValue(AUTH_COOKIE_NAMES.accessTokenExpiry);
    try {
      return await locks.request(REFRESH_LOCK_NAME, async () => {
        // Another tab already refreshed the shared cookie session; rotating again would 401 that tab's in-flight requests.
        const expiryNow = getBrowserCookieValue(AUTH_COOKIE_NAMES.accessTokenExpiry);
        const expiryNowMs = expiryNow ? new Date(expiryNow).getTime() : NaN;
        if (!this.refreshToken && expiryNow !== expiryBeforeWait && expiryNowMs > Date.now()) {
          this.scheduleProactiveRefreshFromExpiry(expiryNowMs);
          return "ok";
        }
        return this.sendRefreshRequest();
      });
    } catch {
      // The lock can be refused (e.g. storage-partitioned contexts); callers rely on this method never rejecting.
      return this.sendRefreshRequest();
    }
  }

  private async sendRefreshRequest(): Promise<RefreshResult> {
    try {
      const csrfToken = getBrowserCookieValue(AUTH_COOKIE_NAMES.csrf);
      const response = await fetch(`${this.baseUrl}/auth/refreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-App-Platform": this.platform,
          ...(this.deviceId && { "x-device-id": this.deviceId }),
          ...(csrfToken && { "X-CSRF-Token": csrfToken }),
        },
        credentials: "include",
        body: this.refreshToken
          ? JSON.stringify({ refreshToken: this.refreshToken })
          : undefined,
        // A hung refresh would otherwise hold the cross-tab lock and stall every other tab's refresh.
        ...(typeof AbortSignal !== "undefined" &&
          typeof AbortSignal.timeout === "function" && { signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS) }),
      });

      // Rate limits and outages are transient, unless the server also wiped the cookie session (its refresh catch-all does).
      if (response.status === 429 || response.status >= 500) {
        return this.refreshToken || this.hasBrowserSession() ? "network" : "rejected";
      }

      const data = await response.json();

      if (!response.ok || data.status === "failed") {
        return "rejected";
      }

      const newTokens = data.data || data;

      // Store tokens if present (non-browser path; browser path strips them)
      if (newTokens?.accessToken) {
        this.accessToken = newTokens.accessToken;
        this.scheduleProactiveRefresh(newTokens.accessToken);
      } else if (newTokens?.tokenExpiresIn) {
        // Browser path: tokens stripped, but server may return expiry info.
        // Schedule next proactive refresh from the expiry timestamp.
        const expiresAtMs = new Date(newTokens.tokenExpiresIn).getTime();
        if (expiresAtMs > Date.now()) {
          this.scheduleProactiveRefreshFromExpiry(expiresAtMs);
        }
      }

      if (newTokens?.refreshToken) {
        this.refreshToken = newTokens.refreshToken;
      }

      this.hadBrowserSession = true;
      return "ok";
    } catch {
      return "network";
    }
  }

  /**
   * Emit per-category telemetry when a request fails. Opt-in via `meta.trackingCategory`.
   * Fires for ALL failure classes including ones suppressed by captureNetworkError's
   * Issue filter, giving callers granular funnel signal without forcing global noise.
   */
  private notifyTrackingFailure(
    meta: RequestMeta | undefined,
    endpoint: string,
    method: string,
    errorClass: FailureClass,
    errorReason: string,
  ): void {
    if (!meta?.trackingCategory) return;
    trackFailure(meta.trackingCategory, "fetch_fail", {
      endpoint: endpoint.split("?")[0],
      method,
      error_class: errorClass,
      error_reason: errorReason.slice(0, 200),
    });
  }

  /**
   * Core request method with 401 auto-refresh.
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    meta?: RequestOptions,
    attempt = 0,
    authRetry = 0,
  ): Promise<ApiResponse<T>> {
    // Short-circuit once the session is confirmed dead. The page is mid-
    // redirect to /auth/login; letting queued requests fly just produces more
    // 401s and restarts the refresh cycle before the navigation completes.
    if (this.sessionDead && !this.shouldSkip401(endpoint)) {
      return { success: false, error: SESSION_EXPIRED_ERROR };
    }

    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(endpoint, {
      ...this.getCsrfHeader(options.method),
      ...meta?.headers,
      ...(options.headers as HeadersInit),
    });
    const { signal, cleanup } = requestSignal(meta);
    const retryMeta = meta?.timeoutMs === undefined ? meta : { ...meta, signal, timeoutMs: undefined };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: "include",
        signal,
      });

      // Handle non-JSON responses (429 rate limit, 502 gateway, etc.)
      const contentType = response.headers.get("content-type") || "";
      let data: Record<string, unknown>;

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = {
          error: text || `Request failed with status ${response.status}`,
        };
      }

      if (!response.ok) {
        // Rate limiting
        if (response.status === 429) {
          return {
            success: false,
            error: "Too many requests. Please wait a moment and try again.",
            // TODO: CHECK WITH TEAM: the shared 429 branch now returns status and the error body to every caller
            status: response.status,
            errorBody: data,
          };
        }

        // 401 Unauthorized, attempt token refresh
        // Skip for login/refresh endpoints
        if (response.status === 401 && !this.shouldSkip401(endpoint)) {
          return this.handleUnauthorized<T>(endpoint, options, retryMeta, authRetry);
        }

        // 403 disabled/deleted account: the account was deactivated (or
        // deleted) after this session's tokens were issued. Unlike a CSRF
        // 403 the session can never recover, so treat it like a failed
        // refresh (session death) instead of letting every widget on the
        // page independently retry and emit its own noisy 403 — a single
        // page mount can fire this for several endpoints at once (see
        // SPEAK-CLIENT-BF/BC/BB/NQ, all the same disabled account within
        // one page load).
        if (this.isAccountDisabledRejection(response.status, data)) {
          this.handleAccountDisabled();
          return {
            success: false,
            error:
              (data.message as string) ||
              "Your account is no longer active. Please contact support.",
            status: response.status,
          };
        }

        // 403 CSRF, the double-submit token is stale, but the session is still
        // valid. Refresh to re-mint it and retry once; never log the user out.
        if (
          attempt < 1 &&
          this.isCsrfRejection(response.status, data) &&
          (await this.recoverFromCsrf(endpoint))
        ) {
          return this.request<T>(endpoint, options, retryMeta, attempt + 1);
        }

        this.captureApiError(endpoint, options.method ?? "GET", response.status, data);

        const errorMsg =
          (data.error as string) ||
          (data.message as string) ||
          `Request failed (${response.status})`;
        this.notifyTrackingFailure(
          meta,
          endpoint,
          options.method ?? "GET",
          "api_error",
          errorMsg,
        );
        return {
          success: false,
          error: errorMsg,
          errorCode:
            typeof data.errorCode === "string" ? data.errorCode : undefined,
          status: response.status,
        };
      }

      // Defensive: some legacy endpoints return HTTP 200 with a body that
      // declares failure ({ status: "failed", message, ... }). Without this
      // check, the caller would treat the failure as success.
      if (data?.status === "failed") {
        const errorMsg =
          (data.message as string) ||
          (data.error as string) ||
          `Request failed (${response.status})`;
        this.notifyTrackingFailure(
          meta,
          endpoint,
          options.method ?? "GET",
          "api_failed",
          errorMsg,
        );
        return {
          success: false,
          error: errorMsg,
          errorCode:
            typeof data.errorCode === "string" ? data.errorCode : undefined,
        };
      }

      // Reset refresh attempts on successful request
      this.refreshAttempts = 0;

      return { success: true, data: (data.data ?? data) as T };
    } catch (error: unknown) {
      const method = options.method ?? "GET";
      const rawMessage = error instanceof Error ? error.message : "Network error";
      const errorClass: FailureClass = isTimeout(error, signal)
        ? "timeout"
        : error instanceof Error && (error.name === "AbortError" || /aborted/i.test(error.message))
          ? "abort"
          : typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "network";

      // Retry an idempotent GET once on a transient network drop before surfacing failure.
      if (errorClass === "network" && method === "GET" && attempt < 1) {
        await new Promise((r) => setTimeout(r, 800));
        return this.request<T>(endpoint, options, retryMeta, attempt + 1);
      }

      this.captureNetworkError(endpoint, method, error);
      this.notifyTrackingFailure(meta, endpoint, method, errorClass, rawMessage);
      return { success: false, error: userMessageForFailure(errorClass) };
    } finally {
      cleanup();
    }
  }

  /**
   * Returns true if the endpoint should skip 401 handling (auth endpoints).
   */
  private shouldSkip401(endpoint: string): boolean {
    return (
      endpoint.includes("/auth/login") ||
      endpoint.includes("/auth/refreshToken") ||
      endpoint.includes("/auth/register") ||
      endpoint.includes("/offers/by-token")
    );
  }

  /**
   * True when a 403 is the CSRF double-submit rejection (a stale/rotated or
   * duplicate CSRF cookie) rather than a genuine permission denial. Matches
   * the server's structured `errorCode` first, falling back to the message
   * string so this works against servers that don't yet emit the code.
   */
  private isCsrfRejection(status: number, data: Record<string, unknown>): boolean {
    return (
      status === 403 &&
      (data?.errorCode === "CSRF_INVALID" ||
        data?.error === "Invalid CSRF token.")
    );
  }

  /**
   * True when a 403 is the server's account-disabled/deleted rejection
   * (`accountDeactivated` in speak-server's is-auth middleware) rather than an
   * ordinary permission denial. The server does not emit a structured
   * `errorCode` for this case, so this matches on the stable message text
   * shared by speak-server's `ACCOUNT_DISABLED` error constant.
   */
  private isAccountDisabledRejection(status: number, data: Record<string, unknown>): boolean {
    return (
      status === 403 &&
      typeof data?.message === "string" &&
      /disabled or deleted/i.test(data.message)
    );
  }

  /**
   * Safety net: recover from a stale CSRF token WITHOUT logging the user out.
   *
   * The server keeps the CSRF token stable across refreshes and evicts stale
   * duplicate cookies, so desync should be rare, but a browser can still drop
   * or block the cookie. A CSRF 403 means the double-submit token is out of
   * sync while the session cookies are still valid, so we must NOT treat it
   * like a 401 (which force-logs-out on failure). Refreshing the session
   * re-mints a matching CSRF cookie; the caller then retries once. CSRF is
   * validated in middleware BEFORE the route handler runs, so the rejected
   * request had NO side effect, retrying is safe even for non-idempotent
   * POSTs. Returns true when the caller should retry.
   */
  private async recoverFromCsrf(endpoint: string): Promise<boolean> {
    if (this.shouldSkip401(endpoint)) return false;
    return this.refreshSession();
  }

  /**
   * Handle a 403 for a disabled/deleted account. Unlike a CSRF 403 this
   * session can never recover, its outcome is the same as a failed token
   * refresh, so reuse the same clearTokens + redirectToLogin path rather than
   * letting the page's other in-flight requests each fail independently.
   * Guarded by `sessionDead` (set synchronously inside redirectToLogin) so a
   * burst of concurrent requests from one page mount only redirects once and
   * emits a single Sentry message instead of one per endpoint.
   */
  private handleAccountDisabled(): void {
    if (this.sessionDead) return;
    this.clearTokens();
    Sentry.captureMessage("Session ended: account disabled or deleted", "warning");
    this.redirectToLogin();
  }

  /**
   * Coordinate token refresh with request queuing.
   * Returns true if refresh succeeded, false if failed (caller should not retry).
   * Handles max-attempts guard, concurrent request queuing, and logout on failure.
   *
   * This is the single source of truth for 401 handling, used by request(),
   * postBlob(), upload(), and postSSEStream().
   */
  private async coordinateTokenRefresh(): Promise<boolean> {
    // Session already marked dead, an earlier 401 cycle failed and the page
    // is mid-redirect. A request whose fetch started before the latch was set
    // can still arrive here after the latch flipped; do NOT kick off a fresh
    // refresh in that window, or we end up with N queued refresh requests
    // racing the browser navigation.
    if (this.sessionDead) {
      return false;
    }

    // No in-memory refresh token is normal for browser cookie sessions; only these two cases have nothing to refresh.
    if (!this.refreshToken && (this.isImpersonationSession() || !this.hasBrowserSession())) {
      this.refreshAttempts = 0;
      this.refreshPromise = null;
      if (!this.isImpersonationSession() && this.hadBrowserSession) {
        this.expireSession("Session expired: no browser session", "cookie");
      }
      return false;
    }

    // Max attempts exceeded, force logout
    if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
      this.refreshAttempts = 0;
      this.refreshPromise = null;
      this.clearTokens();
      Sentry.captureMessage(
        "Session expired: max refresh attempts exceeded",
        "warning",
      );
      this.redirectToLogin();
      return false;
    }

    // If refresh already in progress, wait for it (request queuing)
    if (this.refreshPromise) {
      return (await this.refreshPromise) === "ok";
    }

    // Start refresh, this caller owns the promise
    this.refreshAttempts++;
    const refreshPromise = this.attemptRefresh();
    this.refreshPromise = refreshPromise;

    const result = await refreshPromise;

    // Only clear if we still own the promise, so a later caller's refresh is not dropped.
    if (this.refreshPromise === refreshPromise) {
      this.refreshPromise = null;
    }

    const authMode = this.refreshToken ? "token" : "cookie";
    if (result === "ok") {
      // Reset on every refresh success, not only a fully successful retried
      // request() (below). A dashboard-style page can fire 20+ requests at once;
      // each one's 401 handler that finds no refreshPromise in flight starts its
      // OWN refresh cycle, even moments after an earlier cycle already succeeded,
      // because refreshPromise clears between waves. Without this reset those
      // harmless extra cycles accumulate toward MAX_REFRESH_ATTEMPTS and force a
      // logout despite every refresh call actually succeeding (SPEAK-CLIENT-NY).
      // MAX_AUTH_RETRIES_PER_CALL still bounds a single call that keeps 401ing.
      this.refreshAttempts = 0;
      track("auth", "session_refreshed_after_401", { auth_mode: authMode });
      return true;
    }

    // Keep the session on a transient failure and don't count it toward MAX_REFRESH_ATTEMPTS (meant for refresh-then-401 loops).
    if (result === "network") {
      this.refreshAttempts = Math.max(0, this.refreshAttempts - 1);
      track("auth", "session_refresh_network_fail", { auth_mode: authMode });
      return false;
    }

    // Refresh rejected, force logout
    this.refreshAttempts = 0;
    this.expireSession("Session expired: token refresh failed", authMode);
    return false;
  }

  /** Clears tokens and, unless on a pre-login page, reports the expiry as a warning and redirects to login. */
  private expireSession(reason: string, authMode: "token" | "cookie"): void {
    this.clearTokens();
    // Pre-login pages have nothing to return to and must stay usable for signing in.
    if (typeof window !== "undefined" && isAuthRoute(window.location.pathname)) return;
    Sentry.captureMessage(reason, { level: "warning", tags: { auth_mode: authMode } });
    this.redirectToLogin();
  }

  /** Prod cookie sessions never hold an access token in memory; only the admin impersonation handoff sets one without a refresh token. */
  private isImpersonationSession(): boolean {
    return Boolean(this.accessToken) && !this.refreshToken;
  }

  /** The CSRF cookie is the JS-readable marker of a cookie session and lives as long as its refresh token. */
  private hasBrowserSession(): boolean {
    return Boolean(getBrowserCookieValue(AUTH_COOKIE_NAMES.csrf));
  }

  /**
   * Redirect to login page, flushing Sentry in the background.
   *
   * Idempotent: once the `sessionDead` latch is set, subsequent calls no-op
   * so concurrent failures during the ~2s Sentry flush don't trigger a cascade
   * of redirect attempts. The flush is fire-and-forget, waiting for it before
   * navigating lets other components slip in fresh 401s and restart the cycle.
   */
  private redirectToLogin(): void {
    if (this.sessionDead || typeof window === "undefined") return;
    this.sessionDead = true;

    const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    const isProd = import.meta.env.PROD;
    document.cookie = `${AUTH_COOKIE_NAMES.csrf}=; path=/; ${expire}; SameSite=Lax${isProd ? "; Secure; Domain=.speakai.co" : ""}`;

    // Fire-and-forget: flush buffered events but don't block navigation on it.
    void Sentry.flush(2000).catch(() => {});

    if (window.location.pathname.startsWith("/auth/login")) return;

    // Preserve where the user was so re-login returns them there instead of
    // a bare login page with no context. Whatever save/upload was in flight
    // when the session died is still lost either way (this only fixes where
    // the user LANDS, not what was in flight — see SPEAK-CLIENT-44), but a
    // silent hard-redirect with nothing to return to is worse than this.
    //
    // Pathname only — deliberately dropping search/hash. Several routes
    // carry sensitive values in their query string (e.g. an embed/chat
    // token, an OAuth `code` on an integrations callback); echoing those into
    // `/auth/login?returnUrl=...` would leak them into page-view analytics,
    // Sentry breadcrumbs/URLs, and the Referer header sent to the login
    // page's own third-party scripts. Losing "which tab was open" is a much
    // smaller cost than that. Reuses the same canonical helper login-page.tsx
    // already reads this param with, instead of a fourth hand-rolled copy.
    window.location.href = buildLoginRedirectUrl(window.location.pathname);
  }

  /**
   * Handle 401, refresh token and retry. Threads `meta` so the retried
   * request preserves its trackingCategory, telemetry parity matters
   * exactly when a session-edge fetch fails (the highest-signal case).
   *
   * `authRetry` bounds how many times THIS call retries after a successful
   * refresh (MAX_AUTH_RETRIES_PER_CALL), independent of the shared
   * refreshAttempts counter, which resets on every refresh success and so no
   * longer bounds a single endpoint stuck 401ing after the token refreshed.
   */
  private async handleUnauthorized<T>(
    endpoint: string,
    options: RequestInit,
    meta?: RequestOptions,
    authRetry = 0,
  ): Promise<ApiResponse<T>> {
    const refreshed = await this.coordinateTokenRefresh();
    if (refreshed && authRetry < MAX_AUTH_RETRIES_PER_CALL) {
      return this.request<T>(endpoint, options, meta, 0, authRetry + 1);
    }
    return { success: false, error: SESSION_EXPIRED_ERROR };
  }

  /**
   * Send API errors to Sentry. Captures all 5xx responses (level=error) and
   * all 4xx responses except 401 and 429 (level=warning).
   *
   * 401 is excluded: the client auto-refreshes and retries on token expiry,
   * so every normal expiry would produce a noisy Sentry event.
   * 429 is excluded: expected rate-limiting, not an actionable bug.
   * 403 IS captured: permission denials may indicate real authorization bugs.
   * Responses carrying a structured errorCode ARE captured and the code is
   * promoted to a Sentry tag (api_error_code) for filterability.
   *
   * Forwards only sanitized message + errorCode, raw data.error is
   * intentionally not forwarded.
   */
  private captureApiError(
    endpoint: string,
    method: string,
    status: number,
    data: { error?: unknown; message?: unknown; errorCode?: unknown; hints?: unknown },
  ): void {
    const should5xx = status >= 500;
    const should4xx = status >= 400 && status < 500 && status !== 401 && status !== 429;
    if (!should5xx && !should4xx) return;

    const path = endpoint.split("?")[0];
    const groupedPath = normalizeApiPath(path);
    if (isExpectedApiRejection(status, data)) {
      // Stays countable as a log without creating an Issue.
      emitActivityLog(
        "api.expected_rejection",
        { api_status: status, api_endpoint: groupedPath, api_method: method, api_error_code: String(data.errorCode).slice(0, 64) },
        "warn",
      );
      return;
    }
    // Prefer message/error; fall back to the server's `hints` (validation
    // rejections carry the detail there, not in `message`) so 4xx bodies aren't
    // captured with an empty reason.
    const hintsMessage = Array.isArray(data.hints)
      ? data.hints.filter((h): h is string => typeof h === "string").join("; ")
      : typeof data.hints === "string"
        ? data.hints
        : "";
    const rawMessage =
      typeof data.message === "string" && data.message
        ? data.message
        : typeof data.error === "string" && data.error
          ? data.error
          : hintsMessage;
    const sanitizedMessage = rawMessage
      .replace(/\b(pi|ch|cus|seti|sub|in|pm|evt|txn)_[A-Za-z0-9]{14,}\b/g, "[stripe_id]")
      .slice(0, 200);
    const errorCode =
      typeof data.errorCode === "string" ? data.errorCode.slice(0, 64) : undefined;

    Sentry.captureMessage(`API ${status} ${method} ${groupedPath}`, {
      level: status < 500 ? "warning" : "error",
      tags: {
        api_status: String(status),
        api_endpoint: groupedPath,
        api_method: method,
        ...(errorCode && { api_error_code: errorCode }),
      },
      extra: {
        message: sanitizedMessage,
        errorCode,
        path,
      },
    });
  }

  /**
   * High-value endpoints whose failures must never be silently dropped — even a
   * flaky browser network-throw — so outages surface immediately. A backend stall
   * on one of these (e.g. SPEAK-SERVER-1XS: 16 users hit an upload stall with zero
   * client-side Sentry signal) is a page-one incident, not network noise.
   */
  private isCriticalEndpoint(path: string): boolean {
    return /^\/(media\/(process|upload)|payment|subscription)(\/|$)/.test(path);
  }

  /**
   * Capture network failures (fetch threw) to Sentry. Filters out:
   * - AbortError: user-initiated cancellations (route change mid-flight, dialog close)
   * - Offline browser: navigator.onLine===false means the device has no network
   *
   * Browser-network fetch-throws ("Failed to fetch", "NetworkError…", "Load failed")
   * are usually unfixable flaky-network drops (mobile tunnels, WiFi roam). For
   * non-critical endpoints we emit ONE grouped signal per endpoint (a single issue
   * with a user count) instead of per-event noise; for critical endpoints we always
   * capture the full exception so an upload/payment outage can never go invisible.
   */
  private captureNetworkError(endpoint: string, method: string, error: unknown): void {
    if (!(error instanceof Error)) return;
    if (error.name === "AbortError" || /aborted/i.test(error.message)) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;

    const path = endpoint.split("?")[0];
    const isFlakyBrowserThrow =
      /Failed to fetch|NetworkError when attempting|Load failed/i.test(error.message);

    if (isFlakyBrowserThrow && !this.isCriticalEndpoint(path)) {
      // Grouped, low-cardinality signal: spikes (many users, one endpoint) stay
      // visible without flooding Issues with individual flaky-network drops.
      const groupedPath = normalizeApiPath(path);
      Sentry.captureMessage(`Network fail ${method} ${groupedPath}`, {
        level: "warning",
        fingerprint: ["network-fail", method, groupedPath],
        tags: { api_endpoint: groupedPath, api_method: method, network_fail: "true" },
        extra: { path },
      });
      return;
    }

    Sentry.captureException(error, {
      tags: {
        api_endpoint: path,
        api_method: method,
      },
    });
  }

  // -- Public HTTP methods -----------------------------------------

  getBaseUrl(): string {
    return this.baseUrl;
  }

  get<T>(endpoint: string, headers?: HeadersInit, meta?: RequestOptions) {
    return this.request<T>(endpoint, { method: "GET", headers }, meta);
  }

  post<T>(endpoint: string, body?: unknown, headers?: HeadersInit, meta?: RequestOptions) {
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      },
      meta,
    );
  }

  put<T>(endpoint: string, body?: unknown, headers?: HeadersInit, meta?: RequestOptions) {
    return this.request<T>(
      endpoint,
      {
        method: "PUT",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      },
      meta,
    );
  }

  patch<T>(endpoint: string, body?: unknown, headers?: HeadersInit, meta?: RequestOptions) {
    return this.request<T>(
      endpoint,
      {
        method: "PATCH",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      },
      meta,
    );
  }

  delete<T>(endpoint: string, headers?: HeadersInit, meta?: RequestOptions) {
    return this.request<T>(endpoint, { method: "DELETE", headers }, meta);
  }

  /**
   * POST request that returns a raw Blob (for file downloads like invoices).
   */
  async postBlob(endpoint: string, body?: unknown, meta?: RequestMeta, authRetry = 0): Promise<Blob> {
    if (this.sessionDead && !this.shouldSkip401(endpoint)) {
      throw new Error(SESSION_EXPIRED_ERROR);
    }
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(endpoint, this.getCsrfHeader("POST"));
    const response = await fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      // 401 Unauthorized, use shared refresh handler
      if (response.status === 401 && !this.shouldSkip401(endpoint)) {
        const refreshed = await this.coordinateTokenRefresh();
        if (refreshed && authRetry < MAX_AUTH_RETRIES_PER_CALL) {
          return this.postBlob(endpoint, body, meta, authRetry + 1);
        }
        throw new Error(SESSION_EXPIRED_ERROR);
      }

      const contentType = response.headers.get("content-type") || "";
      let errMessage = `Export failed (${response.status})`;
      let errData: Record<string, unknown> = {};
      if (contentType.includes("application/json")) {
        errData = await response.json().catch(() => ({}));
        errMessage =
          (errData.error as string) ||
          (errData.message as string) ||
          errMessage;
      }

      this.captureApiError(endpoint, "POST", response.status, errData);
      this.notifyTrackingFailure(meta, endpoint, "POST", "api_error", errMessage);
      throw new Error(errMessage);
    }

    return response.blob();
  }

  /**
   * POST request that returns an SSE (Server-Sent Events) stream.
   */
  async *postSSEStream<T>(
    endpoint: string,
    body: unknown,
    signal?: AbortSignal,
    meta?: RequestMeta,
  ): AsyncGenerator<T> {
    try {
      yield* this.postSSEStreamInner<T>(endpoint, body, signal, meta);
    } catch (error) {
      this.notifyTrackingFailure(
        meta,
        endpoint,
        "POST",
        classifySSEError(error),
        error instanceof Error ? error.message : String(error),
      );
      throw error;
    }
  }

  /**
   * Inner SSE implementation, wrapped by postSSEStream for centralized
   * trackFailure emission. Throws preserve native AbortError DOMException
   * so consumers' existing abort handling still works.
   */
  private async *postSSEStreamInner<T>(
    endpoint: string,
    body: unknown,
    signal?: AbortSignal,
    meta?: RequestMeta,
    authRetry = 0,
  ): AsyncGenerator<T> {
    if (this.sessionDead && !this.shouldSkip401(endpoint)) {
      throw new Error(SESSION_EXPIRED_ERROR);
    }
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(endpoint, {
      Accept: "text/event-stream",
      ...this.getCsrfHeader("POST"),
    });

    // Retry the initial fetch up to twice on TypeError ("Failed to fetch") to ride
    // through brief cellular handoffs and proxy hiccups. AbortError (DOMException)
    // and other non-TypeError errors propagate unchanged so the caller's existing
    // abort handling stays correct. Discriminant strings ("offline" /
    // "network_after_retry") let chat-dialog render targeted bubble copy.
    //
    // Two retries (not one): SPEAK-CLIENT-71 showed 44 users / 72 events hitting
    // "network_after_retry", and the event breakdown skewed desktop Chrome/Windows
    // in short repeated bursts, not the single mobile-handoff blip this backoff was
    // originally sized for. A lone 800ms retry doesn't give a flaky corporate
    // proxy/VPN hop time to recover; a second retry with a longer backoff does,
    // without materially changing the worst-case wait for a truly dead connection.
    const MAX_SSE_CONNECT_RETRIES = 2;
    const SSE_RETRY_BACKOFF_MS = 800;
    let attempt = 0;
    let response: Response;
    while (true) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new Error("offline");
      }
      try {
        response = await fetch(url, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify(body),
          signal,
        });
        break;
      } catch (err) {
        if (!(err instanceof TypeError)) throw err;
        attempt++;
        if (attempt > MAX_SSE_CONNECT_RETRIES) throw new Error("network_after_retry");
        // Backoff is NOT abort-aware, if signal aborts during the wait,
        // the next loop iteration's fetch() throws AbortError immediately.
        // Worst-case one backoff window's delay before abort propagates; acceptable.
        // Increases per attempt (800ms, then 1600ms) rather than a flat wait, giving
        // a slower-to-recover connection more room on the second try.
        await new Promise((r) => setTimeout(r, SSE_RETRY_BACKOFF_MS * attempt));
      }
    }
    if (attempt > 0) {
      track("ai_chat", "stream_retry_recovered", { attempt_ms: SSE_RETRY_BACKOFF_MS * attempt });
    }

    if (!response.ok) {
      // 401 Unauthorized, use shared refresh handler, then retry
      if (response.status === 401 && !this.shouldSkip401(endpoint)) {
        const refreshed = await this.coordinateTokenRefresh();
        if (refreshed && authRetry < MAX_AUTH_RETRIES_PER_CALL) {
          yield* this.postSSEStreamInner<T>(endpoint, body, signal, meta, authRetry + 1);
          return;
        }
        throw new Error(SESSION_EXPIRED_ERROR);
      }

      const errorData = await response.json().catch(() => ({}));
      this.captureApiError(endpoint, "POST", response.status, errorData as Record<string, unknown>);
      throw new Error(
        (errorData as Record<string, string>).error ||
          (errorData as Record<string, string>).message ||
          `SSE request failed with status ${response.status}`,
      );
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("Response body is not readable");

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed.startsWith(":")) continue;

          if (trimmed.startsWith("data:")) {
            const jsonStr = trimmed.slice(5).trim();
            if (jsonStr === "[DONE]") return;

            try {
              yield JSON.parse(jsonStr) as T;
            } catch {
              // Non-JSON data line, skip
            }
          }
        }
      }

      if (buffer.trim().startsWith("data:")) {
        const jsonStr = buffer.trim().slice(5).trim();
        if (jsonStr && jsonStr !== "[DONE]") {
          try {
            yield JSON.parse(jsonStr) as T;
          } catch {}
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Upload a file with multipart/form-data, POST unless `method` says otherwise.
   */
  async upload<T>(
    endpoint: string,
    formData: FormData,
    method: "POST" | "PUT" = "POST",
    authRetry = 0,
  ): Promise<ApiResponse<T>> {
    if (this.sessionDead && !this.shouldSkip401(endpoint)) {
      return { success: false, error: SESSION_EXPIRED_ERROR };
    }
    const url = `${this.baseUrl}${endpoint}`;
    const { "Content-Type": _ct, ...headersWithoutContentType } =
      this.getHeaders(endpoint, this.getCsrfHeader(method)) as Record<string, string>;

    try {
      const response = await fetch(url, {
        method,
        headers: headersWithoutContentType,
        credentials: "include",
        body: formData,
      });

      // 401 Unauthorized, use shared refresh handler
      if (!response.ok && response.status === 401) {
        const refreshed = await this.coordinateTokenRefresh();
        if (refreshed && authRetry < MAX_AUTH_RETRIES_PER_CALL) {
          return this.upload<T>(endpoint, formData, method, authRetry + 1);
        }
        return {
          success: false,
          error: SESSION_EXPIRED_ERROR,
        };
      }

      const isJson = (response.headers.get("content-type") || "").includes("application/json");
      const data = isJson ? await response.json() : {};

      if (!response.ok) {
        this.captureApiError(endpoint, method, response.status, data);
        return {
          success: false,
          error: data.error || data.message || "Upload failed",
          status: response.status,
        };
      }

      return { success: true, data: data.data ?? data };
    } catch (error: unknown) {
      this.captureNetworkError(endpoint, method, error);
      const errorClass: FailureClass =
        error instanceof Error && (error.name === "AbortError" || /aborted/i.test(error.message))
          ? "abort"
          : typeof navigator !== "undefined" && navigator.onLine === false
            ? "offline"
            : "network";
      return { success: false, error: userMessageForFailure(errorClass) };
    }
  }
}

export const apiClient = new ApiClient(API_URL);
